import { useEffect, useState, FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import axiosInstance from '../../../lib/axios';
import { CreditTransaction, RecordPaymentPayload } from '../../../types/credit';

const NOTE_MAX_LENGTH = 500;

interface RecordPaymentModalProps {
  open: boolean;
  onClose: () => void;
  accountId: string;
  balance?: number | null;
}

const formatCurrency = (amount: number | null | undefined) => {
  if (amount === null || amount === undefined) return '—';
  return new Intl.NumberFormat('fr-DZ', {
    style: 'currency',
    currency: 'DZD',
    minimumFractionDigits: 0,
  }).format(amount);
};

// The backend answers an overpayment with a 400 whose message names both figures
// ("Payment of X exceeds the outstanding balance of Y…"), so it is shown verbatim rather than
// replaced by a generic key. class-validator failures arrive as an array of strings.
const extractApiMessage = (error: unknown): string | null => {
  const message = (error as { response?: { data?: { message?: string | string[] } } })?.response
    ?.data?.message;
  if (Array.isArray(message)) return message.join(', ');
  return typeof message === 'string' && message.length > 0 ? message : null;
};

export const RecordPaymentModal = ({
  open,
  onClose,
  accountId,
  balance,
}: RecordPaymentModalProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setAmount('');
      setNote('');
      setSubmitted(false);
      setApiError(null);
    }
  }, [open]);

  const parsedAmount = Number(amount);
  const amountInvalid = amount.trim() === '' || !Number.isFinite(parsedAmount) || parsedAmount <= 0;
  const noteInvalid = note.length > NOTE_MAX_LENGTH;

  const recordPaymentMutation = useMutation({
    mutationFn: async (payload: RecordPaymentPayload): Promise<CreditTransaction> => {
      const response = await axiosInstance.post<CreditTransaction>(
        `/credit/accounts/${accountId}/payments`,
        payload,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credit-account', accountId] });
      queryClient.invalidateQueries({ queryKey: ['credit-accounts'] });
      onClose();
    },
    onError: (error: unknown) => {
      setApiError(extractApiMessage(error) || t('credit.recordPayment.error'));
    },
  });

  const isSubmitting = recordPaymentMutation.isPending;

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    setApiError(null);
    if (amountInvalid || noteInvalid || !accountId) return;
    recordPaymentMutation.mutate({
      amount: parsedAmount,
      note: note.trim() ? note.trim() : undefined,
    });
  };

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{t('credit.recordPayment.title')}</Typography>
          <IconButton onClick={handleClose} size="small" disabled={isSubmitting}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Stack spacing={2.5}>
            {apiError && <Alert severity="error">{apiError}</Alert>}

            <Box>
              <Typography variant="body2" color="text.secondary">
                {t('credit.recordPayment.currentBalance')}
              </Typography>
              <Typography variant="h6" fontWeight={700}>
                {formatCurrency(balance)}
              </Typography>
            </Box>

            <TextField
              value={amount}
              onChange={event => setAmount(event.target.value)}
              type="number"
              label={t('credit.recordPayment.amount')}
              fullWidth
              required
              autoFocus
              inputProps={{ min: 0, step: '0.01' }}
              error={submitted && amountInvalid}
              helperText={submitted && amountInvalid ? t('credit.recordPayment.amountInvalid') : ''}
              disabled={isSubmitting}
            />

            <TextField
              value={note}
              onChange={event => setNote(event.target.value)}
              label={t('credit.recordPayment.note')}
              fullWidth
              multiline
              rows={2}
              inputProps={{ maxLength: NOTE_MAX_LENGTH }}
              error={noteInvalid}
              helperText={
                noteInvalid
                  ? t('credit.recordPayment.noteTooLong', { max: NOTE_MAX_LENGTH })
                  : `${note.length}/${NOTE_MAX_LENGTH}`
              }
              disabled={isSubmitting}
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} disabled={isSubmitting}>
            {t('credit.recordPayment.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting || !accountId}
            startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
          >
            {t('credit.recordPayment.submit')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default RecordPaymentModal;
